import {
    getPcLevelIcon,
    getStringFromGenresArray,
    isSmartTV,
    parseVodId,
} from '@/shared/lib'
import { Box, Typography } from '@mui/material'
import React, { lazy, memo, useEffect, useRef, useState } from 'react'
import { IAssetsContent } from '@/types/vod/VodItemDetails'
import { getVodAssetTypeViewStyles } from '@/features/VOD/styles/VodAssetTypeViewStyles'
import { DisplayAgeRating } from '@/shared/ui/DisplayAgeRating'
import { useAppStore, useVODStore } from '@/shared/store'
import { VodRowRecommendationsContainer } from '@/features/VOD/components/VodRowRecommendationsContainer'
import {
    cutvType,
    vodType,
    vodTypeEpisodes,
    vodTypeSeries,
} from '@/shared/constants/vodConstants'
import { selectSelectedVOD } from '@/shared/store/storeSelectors/vodSelectors'
import { usePinVerification } from '@/widgets/ProfileScreen/hooks/usePinVerification'
import { VodRowViewSkeleton } from '../../skeletons/VodRowViewSkeleton'
import DetailsButtons from '@/features/VOD/components/itemDetails/vodAssetType/DetailsButtons'
import { useTranslation } from 'react-i18next'
import FilmOverviewInfos from '../components/FilmOverviewInfos'
import CastContainer from '../components/CastContainer'
import PinModal from './PinModal'
import ReadMore from '@/components/tv/components/guideView/programGuideView/components/ReadMore'
import { VodRowSeriesContainer } from '../../VodRowSeriesContainer'
import { getVODRecommendationHook } from '@/shared/getContainer/getVODRecommendationHook'
import { useGetVodDetailsContext } from '@/features/VOD/context'
import { getStreamIQConstants } from '@/shared/getContainer/getStreamIQConstants'

const getRecommendationsHook = await getVODRecommendationHook()
const constant = await getStreamIQConstants()
const { EPISODES_TYPE, MOVIE_TYPE, SHOW_TYPE } = constant || {}

const PlayerComponent = lazy(() => import('../../PlayerComponent'))

const VodAssetTypeView = memo(({ details, assetType }: IAssetsContent) => {
    const { t: translate } = useTranslation()
    const {
        title,
        year,
        cast,
        country,
        duration,
        price,
        genres,
        additionalRating,
        pcLevel,
        productId,
        productType,
        isPurchased,
        trailerProductId,
        coverUrl,
        isFavorite,
        offers,
        seasons,
        episodePosition,
        serviceName,
    } = details

    const selectedVOD = useVODStore(selectSelectedVOD)
    const { actualSeason, actualEpisode } = useVODStore()

    const {
        controlButtonsStyles,
        ageRatingContainer,
        mainContainerStyle,
        contentStyle,
        posterStyle,
        leftBox,
        rightBox,
    } = getVodAssetTypeViewStyles(details)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const { actualProfile } = useAppStore()
    const { shouldCheckPin } = usePinVerification()

    const container = useRef<HTMLDivElement | null>(null)

    const mainScreen = document.getElementById('MainScreen')

    const { id: profileId } = actualProfile ?? {}

    const { vodCategoryRows } = getRecommendationsHook(
        profileId,
        productId,
        productType
    )
    const { getItemDetails } = useGetVodDetailsContext()
    const fetchDetails = () =>
        getItemDetails(
            {
                productId: parseVodId(productId as string),
                assetType: productType,
                id: productId as string,
                serviceName: serviceName || '',
            },
            true
        )

    const handleOpenModal = (isPinValid?: boolean) => {
        setIsModalOpen((prev) => !prev)
        isModalOpen && isPinValid && fetchDetails()
    }

    useEffect(() => {
        if (mainScreen) {
            setTimeout(() => {
                mainScreen.style.backgroundImage = `linear-gradient(to bottom, transparent 0%, black),
            ${`url(${details.atmosphericUrl || details.coverUrl})`}`
            }, 10)
        }
        if (details.productId) {
            container.current?.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            })
        }

        return () => {
            if (mainScreen) {
                mainScreen.style.backgroundImage = ''
            }
        }
    }, [details.productId, mainScreen])

    const recommendationTileStyle = {
        mt: { sx: '16px', md: '20px', lg: '32px' },
        mb: { sx: '8px', lg: '16px' },
    }
    const shouldShowPoster =
        productType === vodType ||
        productType === cutvType ||
        productType === MOVIE_TYPE

    const isVod =
        assetType === vodType ||
        assetType === MOVIE_TYPE ||
        assetType === SHOW_TYPE

    const isSeriesOrEpisodes =
        assetType === vodTypeSeries ||
        assetType === vodTypeEpisodes ||
        assetType === SHOW_TYPE

    if (productType && productType === EPISODES_TYPE) {
        return (
            <Box width="100%" height="100%">
                <PlayerComponent
                    assetsType={productType}
                    title={title || ''}
                    isFullScreen={isSmartTV()}
                />
            </Box>
        )
    }

    return (
        <>
            {selectedVOD && productType ? (
                <Box width="100%" height="100%">
                    <PlayerComponent
                        assetsType={productType}
                        title={title || ''}
                        isFullScreen={isSmartTV()}
                    />
                </Box>
            ) : (
                <Box position="relative" height="100%" width="100%">
                    <Box
                        sx={mainContainerStyle}
                        paddingLeft={!coverUrl ? '32px' : ''}
                        ref={container}>
                        <Box sx={contentStyle}>
                            {shouldShowPoster && (
                                <Box sx={leftBox}>
                                    <Box sx={posterStyle}>
                                        <img
                                            src={coverUrl}
                                            alt={`${title} move poster`}
                                        />
                                    </Box>
                                </Box>
                            )}

                            <Box sx={rightBox}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    position="relative"
                                    zIndex={1}
                                    pb="44px">
                                    <Box sx={controlButtonsStyles}>
                                        {assetType !== SHOW_TYPE && (
                                            <DetailsButtons
                                                details={{
                                                    serviceName,
                                                    productId,
                                                    productType,
                                                    isPurchased,
                                                    trailerProductId,
                                                    isFavorite,
                                                }}
                                                callback={handleOpenModal}
                                            />
                                        )}
                                    </Box>

                                    <Typography variant="headline3">
                                        {title}
                                    </Typography>
                                </Box>
                                <Box>
                                    <FilmOverviewInfos
                                        year={year}
                                        genres={genres}
                                        country={country}
                                        duration={duration}
                                    />
                                    {cast?.length && (
                                        <CastContainer cast={cast} />
                                    )}
                                    <>
                                        {genres && (
                                            <Typography>
                                                {getStringFromGenresArray(
                                                    genres
                                                )}
                                            </Typography>
                                        )}
                                    </>
                                    <br />
                                    {Boolean(price) && (
                                        <Typography
                                            position="relative"
                                            zIndex={1}>
                                            {price}
                                        </Typography>
                                    )}
                                    {Boolean(details) && (
                                        <Box
                                            position="relative"
                                            zIndex={1}
                                            maxWidth="50%">
                                            <ReadMore
                                                maxTextLength={550}
                                                text={details.description ?? ''}
                                            />
                                        </Box>
                                    )}
                                    <Box sx={ageRatingContainer}>
                                        {pcLevel
                                            ? getPcLevelIcon(pcLevel)
                                            : null}
                                        <Box
                                            position="relative"
                                            zIndex={1}
                                            display="flex"
                                            columnGap="20px">
                                            {additionalRating &&
                                                DisplayAgeRating(
                                                    additionalRating
                                                )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {isSeriesOrEpisodes && seasons?.length && (
                            <Box maxWidth="90%">
                                {seasons?.length ? (
                                    <VodRowSeriesContainer
                                        seasons={seasons}
                                        episodePosition={
                                            episodePosition || {
                                                seasonId:
                                                    actualSeason?.id || '',
                                                episodeId:
                                                    actualEpisode?.productId ||
                                                    '',
                                            }
                                        }
                                    />
                                ) : (
                                    <VodRowViewSkeleton
                                        maxHeight={258}
                                        rows={1}
                                        items={7}
                                    />
                                )}
                            </Box>
                        )}

                        {productType !== cutvType && isVod && (
                            <Box>
                                {vodCategoryRows.length ? (
                                    <>
                                        <Typography
                                            variant="label1"
                                            component="p"
                                            sx={recommendationTileStyle}>
                                            {translate('recommendations')}
                                        </Typography>
                                        <VodRowRecommendationsContainer
                                            vodRows={vodCategoryRows}
                                        />
                                    </>
                                ) : (
                                    <VodRowViewSkeleton
                                        maxHeight={258}
                                        rows={1}
                                        items={7}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    <PinModal
                        shouldCheckPin={shouldCheckPin}
                        setIsModalOpen={setIsModalOpen}
                        isModalOpen={isModalOpen}
                        handleOpenModal={handleOpenModal}
                        fetchDetails={fetchDetails}
                        offers={offers}
                        productId={productId}
                    />
                </Box>
            )}
        </>
    )
})

VodAssetTypeView.displayName = 'VodAssetTypeView'
export default VodAssetTypeView
